// Tag-Nacht-Wechsel als Ansicht der Welt-Uhr: Himmel, Nebel, Sonne und Hemisphärenlicht werden
// allein aus `daylight(fraction)` aus shared abgeleitet (ADR 0004: keine eigenen Schwellen).
// `daylightColors` ist rein und getestet; `applyDaylight` schreibt das Ergebnis je Bild auf die
// Three.js-Objekte. Kein Zustand liegt in den Objekten (Architektur-Regel 1).

import { Color, Fog, SRGBColorSpace, type DirectionalLight, type HemisphereLight, type Scene } from 'three';
import { clamp, daylight, lerp } from '@bauernhof/shared';

/** Farbe als drei Kanäle in [0, 1], sRGB wie in einem Hex-Wert; die Umrechnung macht `applyDaylight`. */
export type Rgb = readonly [number, number, number];

/** Himmel: helles Blau am Tag, Orange-Rosa in der Dämmerung, dunkles Blau in der Nacht. */
const HIMMEL = { tag: hex(0x87b8e6), daemmerung: hex(0xf2997a), nacht: hex(0x0b1230) } as const;
/** Sonnenlicht: warmes Weiß am Tag, Orange in der Dämmerung, bläuliches Mondlicht in der Nacht. */
const SONNE = { tag: hex(0xfff1d6), daemmerung: hex(0xffa060), nacht: hex(0x8fa3d9) } as const;
/** Hemisphäre: Himmelanteil (von oben) und Bodenanteil (von unten). */
const HEMI_HIMMEL = { tag: hex(0xdfeeff), daemmerung: hex(0xf6b48f), nacht: hex(0x3a4a78) } as const;
const HEMI_BODEN = { tag: hex(0x6b5a3a), nacht: hex(0x23262f) } as const;

/** Lichtstärken am Tag (wie in scene.ts) und in der Nacht: die Summe bleibt über einem Viertel. */
const SONNE_STAERKE = { tag: 1.6, nacht: 0.15 } as const;
const HEMI_STAERKE = { tag: 1.0, nacht: 0.55 } as const;
/** Wie stark die Dämmerungsfarbe in der Mitte der Dämmerung durchschlägt (0 = gar nicht). */
const DAEMMERUNG_STAERKE = { himmel: 0.85, sonne: 0.7, hemi: 0.5 } as const;
/**
 * Nachts wird der Bodenanteil der Hemisphäre zum Himmelanteil hin angehoben: Das Licht wird
 * gleichmäßiger, Seiten und Unterseiten bleiben sichtbar, statt im Dunkeln zu versinken.
 */
const AMBIENT_BOOST_NACHT = 0.6;
/** Nebel: tagsüber hinter dem Dorf (wie in scene.ts), nachts näher, aber nicht vor der Nase. */
const NEBEL = { tag: { nah: 56, fern: 150 }, nacht: { nah: 36, fern: 100 } } as const;
/** Die Sonne steht immer leicht im Süden (+z), damit Nord- und Südseiten unterscheidbar bleiben. */
const SONNE_SUEDNEIGUNG = 0.25;
/** Kleinste Höhe der Lichtrichtung; darunter bekämen Oberseiten kein gerichtetes Licht mehr. */
const SONNE_MINDESTHOEHE = 0.35;

export interface DaylightColors {
  /** Himmelsfarbe, zugleich Nebelfarbe. */
  sky: Rgb;
  sunColor: Rgb;
  /** Richtung, aus der das Sonnenlicht kommt (Einheitsvektor), siehe `sunDirection`. */
  sunDirection: readonly [number, number, number];
  sunIntensity: number;
  hemiSky: Rgb;
  hemiGround: Rgb;
  hemiIntensity: number;
  /** Anteil in [0, 1], um den der Bodenanteil der Hemisphäre zum Himmelanteil angehoben ist. */
  ambientBoost: number;
  fogNear: number;
  fogFar: number;
}

/**
 * Alle Lichtwerte zur Tageszeit `fraction` (0 = Mitternacht, 0.5 = Mittag). Grundlage ist die
 * Helligkeit `d = daylight(fraction)`; die Dämmerungsfarbe wiegt `4·d·(1 − d)`, also 0 bei Tag
 * und Nacht und 1 in der Mitte der Dämmerung. Weil `daylight` symmetrisch um den Mittag ist, sind
 * es alle Farben und Stärken auch; nur die Sonnenrichtung wandert von Ost nach West.
 * Tageszeiten außerhalb von [0, 1] werden wie in `daylight` geklemmt; `dayFraction` liefert
 * ohnehin nur Werte aus [0, 1).
 */
export function daylightColors(fraction: number): DaylightColors {
  const d = daylight(fraction);
  const tageszeit = clamp(fraction, 0, 1);
  const daemmerung = 4 * d * (1 - d);

  const sky = mischeMitDaemmerung(HIMMEL.nacht, HIMMEL.tag, HIMMEL.daemmerung, d, daemmerung * DAEMMERUNG_STAERKE.himmel);
  const sunColor = mischeMitDaemmerung(SONNE.nacht, SONNE.tag, SONNE.daemmerung, d, daemmerung * DAEMMERUNG_STAERKE.sonne);
  const hemiSky = mischeMitDaemmerung(
    HEMI_HIMMEL.nacht,
    HEMI_HIMMEL.tag,
    HEMI_HIMMEL.daemmerung,
    d,
    daemmerung * DAEMMERUNG_STAERKE.hemi,
  );
  const ambientBoost = (1 - d) * AMBIENT_BOOST_NACHT;
  const hemiGround = mische(mische(HEMI_BODEN.nacht, HEMI_BODEN.tag, d), hemiSky, ambientBoost);

  return {
    sky,
    sunColor,
    sunDirection: sunDirection(tageszeit),
    sunIntensity: lerp(SONNE_STAERKE.nacht, SONNE_STAERKE.tag, d),
    hemiSky,
    hemiGround,
    hemiIntensity: lerp(HEMI_STAERKE.nacht, HEMI_STAERKE.tag, d),
    ambientBoost,
    fogNear: lerp(NEBEL.nacht.nah, NEBEL.tag.nah, d),
    fogFar: lerp(NEBEL.nacht.fern, NEBEL.tag.fern, d),
  };
}

/**
 * Richtung, aus der das Licht kommt, als Einheitsvektor: Die Sonne geht bei 0.25 im Osten (+x)
 * auf, steht bei 0.5 im Zenit und geht bei 0.75 im Westen (−x) unter. Die Höhe fällt nie unter
 * `SONNE_MINDESTHOEHE`, damit Oberseiten immer gerichtetes Licht bekommen; nachts wandert der
 * (schwache) Lichtpunkt darum flach am Horizont zurück nach Osten, stetig und ohne Sprung.
 * Werte außerhalb von [0, 1] werden geklemmt, wie in `daylightColors`.
 */
export function sunDirection(fraction: number): readonly [number, number, number] {
  const winkel = (clamp(fraction, 0, 1) - 0.25) * 2 * Math.PI;
  const x = Math.cos(winkel);
  const y = Math.max(Math.sin(winkel), SONNE_MINDESTHOEHE);
  const z = SONNE_SUEDNEIGUNG;
  const laenge = Math.hypot(x, y, z);
  return [x / laenge, y / laenge, z / laenge];
}

export interface DaylightTargets {
  scene: Scene;
  sun: DirectionalLight;
  hemi: HemisphereLight;
  fog: Fog | null;
}

/** Schreibt Himmel, Nebel, Sonne und Hemisphäre zur Tageszeit `fraction` auf die Objekte. */
export function applyDaylight(targets: DaylightTargets, fraction: number): void {
  const w = daylightColors(fraction);
  const { scene, sun, hemi, fog } = targets;

  if (scene.background instanceof Color) {
    setzeFarbe(scene.background, w.sky);
  } else {
    scene.background = setzeFarbe(new Color(), w.sky);
  }
  if (fog !== null) {
    setzeFarbe(fog.color, w.sky);
    fog.near = w.fogNear;
    fog.far = w.fogFar;
  }
  setzeFarbe(sun.color, w.sunColor);
  sun.intensity = w.sunIntensity;
  // Das Richtungslicht scheint von seiner Position zum Ziel im Ursprung; die Richtung genügt.
  sun.position.set(w.sunDirection[0], w.sunDirection[1], w.sunDirection[2]);
  setzeFarbe(hemi.color, w.hemiSky);
  setzeFarbe(hemi.groundColor, w.hemiGround);
  hemi.intensity = w.hemiIntensity;
}

/** Sammelt die Ziele aus einer Szenenansicht wie `SceneView`; Nebel nur, wenn er ein `Fog` ist. */
export function createDaylightTargets(view: { scene: Scene; sun: DirectionalLight; hemisphere: HemisphereLight }): DaylightTargets {
  return {
    scene: view.scene,
    sun: view.sun,
    hemi: view.hemisphere,
    fog: view.scene.fog instanceof Fog ? view.scene.fog : null,
  };
}

/** Hex-Wert wie 0x87b8e6 als drei sRGB-Kanäle in [0, 1]. */
function hex(wert: number): Rgb {
  return [((wert >> 16) & 0xff) / 255, ((wert >> 8) & 0xff) / 255, (wert & 0xff) / 255];
}

function mische(a: Rgb, b: Rgb, t: number): Rgb {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/** Erst Nacht → Tag nach Helligkeit `d`, dann um `gewicht` zur Dämmerungsfarbe hin. */
function mischeMitDaemmerung(nacht: Rgb, tag: Rgb, daemmerung: Rgb, d: number, gewicht: number): Rgb {
  return mische(mische(nacht, tag, d), daemmerung, gewicht);
}

/** Die Kanäle sind sRGB (wie ein Hex-Wert); Three.js rechnet sie in seinen Arbeitsfarbraum um. */
function setzeFarbe(ziel: Color, [r, g, b]: Rgb): Color {
  return ziel.setRGB(r, g, b, SRGBColorSpace);
}
